import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { talleresAPI, solicitudesAPI, finanzasAPI, notificacionesAPI, gestoriaAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import NotificationBell from '../../components/NotificationBell';
import { 
  Users, FileText, DollarSign, Bell, Building, 
  ArrowRight, AlertCircle, CheckCircle, Clock, Upload, Download, HardDrive, Scale, Globe2
} from 'lucide-react';

const AdminDashboard = () => {
  const { user, isAdminFinanzas } = useAuth();
  const [stats, setStats] = useState({
    talleres: 0,
    solicitudesPendientes: 0,
    balanceUSD: 0,
    balanceARS: 0,
    gestoriaNoLeidas: 0,
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  // Acceso a Gestoría: super_admin, admin y admin_finanzas (Feb 2026: extendido para admin_finanzas).
  const hasGestoriaAccess = user && ['super_admin', 'admin', 'admin_finanzas'].includes(user.role);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      // Si es admin_finanzas, solo cargar datos de finanzas + Gestoría
      if (isAdminFinanzas()) {
        const finanzasRes = await finanzasAPI.getResumen('todo');
        let gestoriaNoLeidas = 0;
        if (hasGestoriaAccess) {
          try {
            const stRes = await gestoriaAPI.adminStats();
            gestoriaNoLeidas = stRes.data?.no_leidos || 0;
          } catch {}
        }
        setStats({
          talleres: 0,
          solicitudesPendientes: 0,
          balanceUSD: finanzasRes.data.totales?.balance_usd || 0,
          balanceARS: finanzasRes.data.totales?.balance_ars || 0,
          gestoriaNoLeidas,
        });
      } else {
        const [talleresRes, solicitudesRes, finanzasRes, auditoriaRes] = await Promise.all([
          talleresAPI.getAll({ limit: 500 }),
          solicitudesAPI.getSuscripcion({ estado: 'pendiente' }),
          finanzasAPI.getResumen('todo'),
          notificacionesAPI.getAuditoria({ limit: 10 })
        ]);

        // Manejar respuesta paginada o array directo
        const talleresData = talleresRes.data?.data || talleresRes.data || [];
        const talleresCount = Array.isArray(talleresData) ? talleresData.length : 0;

        let gestoriaNoLeidas = 0;
        if (hasGestoriaAccess) {
          try {
            const stRes = await gestoriaAPI.adminStats();
            gestoriaNoLeidas = stRes.data?.no_leidos || 0;
          } catch {}
        }

        setStats({
          talleres: talleresRes.data?.pagination?.total || talleresCount,
          solicitudesPendientes: solicitudesRes.data.length,
          balanceUSD: finanzasRes.data.totales?.balance_usd || 0,
          balanceARS: finanzasRes.data.totales?.balance_ars || 0,
          gestoriaNoLeidas,
        });

        setRecentActivity(auditoriaRes.data);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // Menú completo para super_admin y admin
  const fullMenuItems = [
    { icon: Building, label: 'Talleres', href: '/admin/talleres', color: 'bg-blue-500' },
    { icon: Users, label: 'Usuarios', href: '/admin/usuarios', color: 'bg-teal-500' },
    { icon: FileText, label: 'Documentos', href: '/admin/documentos', color: 'bg-green-500' },
    { icon: HardDrive, label: 'Estado Documentación', href: '/admin/pdf-dashboard', color: 'bg-indigo-500' },
    { icon: Users, label: 'Fabricantes', href: '/admin/fabricantes', color: 'bg-purple-500' },
    { icon: Globe2, label: 'Países', href: '/admin/paises', color: 'bg-teal-600' },
    { icon: DollarSign, label: 'Finanzas', href: '/admin/finanzas', color: 'bg-amber-500' },
    { icon: Bell, label: 'Solicitudes/Compras', href: '/admin/solicitudes', color: 'bg-red-500' },
    { icon: Users, label: 'Capacitaciones', href: '/admin/capacitaciones', color: 'bg-orange-500' },
    ...(hasGestoriaAccess ? [{ icon: Scale, label: 'Gestoría', href: '/admin/gestoria', color: 'bg-violet-600', badge: stats.gestoriaNoLeidas }] : []),
    { icon: Download, label: 'Descargas', href: '/admin/descargas', color: 'bg-cyan-500' },
    { icon: Bell, label: 'Suscripciones', href: '/admin/suscripciones', color: 'bg-rose-500' },
  ];

  // Menú restringido para admin_finanzas: Finanzas + Gestoría (Feb 2026).
  const finanzasMenuItems = [
    { icon: DollarSign, label: 'Finanzas', href: '/admin/finanzas', color: 'bg-amber-500' },
    ...(hasGestoriaAccess ? [{ icon: Scale, label: 'Gestoría', href: '/admin/gestoria', color: 'bg-violet-600', badge: stats.gestoriaNoLeidas }] : []),
  ];

  const menuItems = isAdminFinanzas() ? finanzasMenuItems : fullMenuItems;

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto py-6 px-4">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isAdminFinanzas() ? 'Panel de Finanzas' : 'Panel de Administración'}
            </h1>
            <p className="text-gray-600">Bienvenido, {user?.full_name}</p>
          </div>
          {/* Campanita visible para todos los admins (incluido admin_finanzas, por Gestoría) */}
          <NotificationBell />
        </div>

        {/* Stats Cards */}
        <div className={`grid ${isAdminFinanzas() ? 'grid-cols-2' : 'grid-cols-2 lg:grid-cols-4'} gap-4 mb-8`}>
          {/* Solo mostrar Talleres y Solicitudes si NO es admin_finanzas */}
          {!isAdminFinanzas() && (
            <>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Talleres</p>
                      <p className="text-2xl font-bold">{stats.talleres}</p>
                    </div>
                    <Building className="h-8 w-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Solicitudes/Compras</p>
                      <p className="text-2xl font-bold">{stats.solicitudesPendientes}</p>
                    </div>
                    {stats.solicitudesPendientes > 0 ? (
                      <AlertCircle className="h-8 w-8 text-red-500" />
                    ) : (
                      <CheckCircle className="h-8 w-8 text-green-500" />
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Balance USD</p>
                  <p className="text-2xl font-bold">${stats.balanceUSD.toFixed(2)}</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Balance ARS</p>
                  <p className="text-2xl font-bold">${stats.balanceARS.toFixed(0)}</p>
                </div>
                <DollarSign className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className={`grid ${isAdminFinanzas() ? 'md:grid-cols-1 max-w-xs' : 'md:grid-cols-5'} gap-4 mb-8`}>
          {menuItems.map((item, index) => (
            <Link key={index} to={item.href}>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer relative">
                {item.badge > 0 && (
                  <Badge className="absolute -top-2 -right-2 bg-red-500 text-white min-w-[24px] h-6 flex items-center justify-center rounded-full shadow-lg z-10">
                    {item.badge > 9 ? '9+' : item.badge}
                  </Badge>
                )}
                <CardContent className="pt-6 text-center">
                  <div className={`${item.color} w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3`}>
                    <item.icon className="h-6 w-6 text-white" />
                  </div>
                  <p className="font-medium">{item.label}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Recent Activity & Pending - Solo para admin completo */}
        {!isAdminFinanzas() && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Solicitudes/Compras Pendientes */}
            {stats.solicitudesPendientes > 0 && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Solicitudes/Compras Pendientes</CardTitle>
                  <Badge variant="destructive">{stats.solicitudesPendientes}</Badge>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">Hay solicitudes o compras esperando tu revisión</p>
                  <Button asChild>
                    <Link to="/admin/solicitudes">
                      Revisar ahora <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Recent Activity */}
            <Card className={stats.solicitudesPendientes > 0 ? '' : 'lg:col-span-2'}>
              <CardHeader>
                <CardTitle className="text-lg">Actividad Reciente</CardTitle>
              </CardHeader>
              <CardContent>
                {recentActivity.length > 0 ? (
                  <div className="space-y-3">
                    {recentActivity.slice(0, 5).map((activity, index) => (
                      <div key={index} className="flex items-start gap-3 py-2 border-b last:border-0">
                        <Clock className="h-4 w-4 text-gray-400 mt-1" />
                        <div className="flex-1">
                          <p className="text-sm">
                            <span className="font-medium">{activity.usuario_nombre || 'Admin'}</span>
                            {' '}
                            <span className="text-gray-600">
                              {activity.accion} {activity.entidad_tipo}
                            </span>
                          </p>
                          <p className="text-xs text-gray-400">
                            {new Date(activity.timestamp).toLocaleString('es-AR')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No hay actividad reciente</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
